import { ClaimWorkflowConfigurationOptions } from '../../api';
import { AutomationWorkerId } from '../../services/worker-configuration/automation/worker-automation.types';

export type ConfigurationWorkerExecutionEntry =
    | { kind: 'automation'; id: AutomationWorkerId }
    | { kind: 'status'; workflowStatus: string };

export type ConfigurationSidebarAutomationWorker = {
    active: boolean;
    desc: string;
    icon: string;
    id: AutomationWorkerId;
    key: string;
    name: string;
    ruleCount: number;
    scopeLabel: string;
};

export type ConfigurationSidebarStatusOption = ClaimWorkflowConfigurationOptions & {
    workflowStatus: string;
};

export type ConfigurationSidebarItem =
    | { kind: 'automation'; worker: ConfigurationSidebarAutomationWorker }
    | { kind: 'status'; status: ConfigurationSidebarStatusOption };

export function buildOrderedConfigurationSidebarItems(
    automationWorkers: ConfigurationSidebarAutomationWorker[],
    configurationOptions: ClaimWorkflowConfigurationOptions[],
): ConfigurationSidebarItem[] {
    const automationById = new Map(automationWorkers.map(worker => [worker.id, worker]));
    const statusByCode = new Map(
        configurationOptions
            .filter((option): option is ConfigurationSidebarStatusOption => Boolean(option.workflowStatus?.trim()))
            .map(option => [option.workflowStatus, option]),
    );
    const seen = new Set<string>();
    const items: ConfigurationSidebarItem[] = [];

    for (const entry of CONFIGURATION_WORKER_EXECUTION_ORDER) {
        if (entry.kind === 'automation') {
            const worker = automationById.get(entry.id);
            if (worker) {
                items.push({ kind: 'automation', worker });
                seen.add(`automation:${entry.id}`);
            }
            continue;
        }

        const status = statusByCode.get(entry.workflowStatus);
        if (status) {
            items.push({ kind: 'status', status });
            seen.add(`status:${entry.workflowStatus}`);
        }
    }

    for (const worker of automationWorkers) {
        if (!seen.has(`automation:${worker.id}`)) {
            items.push({ kind: 'automation', worker });
        }
    }

    const remainingStatuses = configurationOptions
        .filter(
            (option): option is ConfigurationSidebarStatusOption =>
                Boolean(option.workflowStatus?.trim()) && !seen.has(`status:${option.workflowStatus}`),
        )
        .sort((left, right) => compareWorkflowStatusExecutionOrder(left.workflowStatus, right.workflowStatus));

    for (const status of remainingStatuses) {
        items.push({ kind: 'status', status });
    }

    return items;
}

/** Claim workflow workers in the order they execute. */
export const CONFIGURATION_WORKER_EXECUTION_ORDER: ConfigurationWorkerExecutionEntry[] = [
    { kind: 'status', workflowStatus: 'NEEDS_VENDOR_CODING' },
    { kind: 'status', workflowStatus: 'NEEDS_ENTER_CODING' },
    { kind: 'status', workflowStatus: 'EXECUTE_ENTER_CODING' },
    { kind: 'status', workflowStatus: 'WAIT_FOR_PROFESSIONAL_CREATED' },
    { kind: 'status', workflowStatus: 'HALTED' },
    { kind: 'status', workflowStatus: 'TIMELY_FILING' },
    { kind: 'status', workflowStatus: 'NEED_MEDICAL_RECORDS' },
    { kind: 'status', workflowStatus: 'NEED_PRIMARY_AUTHORIZATION_NUMBER' },
    { kind: 'status', workflowStatus: 'NEED_PRIMARY_ELIGIBILITY_CHECK' },
    { kind: 'status', workflowStatus: 'HIGH_VALUE_CLAIM_SPLIT' },
    { kind: 'status', workflowStatus: 'NEED_PRIMARY_TRANSMIT' },
    { kind: 'status', workflowStatus: 'NEED_PRIMARY_NON_ACKNOWLEDGED_TRANSMIT' },
    { kind: 'automation', id: 'rejection' },
    { kind: 'status', workflowStatus: 'REJECTION_UNENROLLED_PAYER' },
    { kind: 'status', workflowStatus: 'NEED_PRIMARY_RETRANSMIT' },
    { kind: 'status', workflowStatus: 'PAYMENT_DENIED_FOR_HOSPICE_CARE' },
    { kind: 'status', workflowStatus: 'PAYMENT_DENIED_FOR_LACK_OF_DOCUMENTATION' },
    { kind: 'status', workflowStatus: 'PAYMENT_DENIED_FOR_NEED_NEW_SUBMISSION' },
    { kind: 'automation', id: 'denial' },
    { kind: 'status', workflowStatus: 'NEED_SECONDARY_TRANSMIT' },
    { kind: 'status', workflowStatus: 'NEED_TERTIARY_TRANSMIT' },
    { kind: 'status', workflowStatus: 'NEED_GUARANTOR_COLLECTIONS' },
    { kind: 'status', workflowStatus: 'DENIED_CLAIM' },
    { kind: 'status', workflowStatus: 'REJECTED_CLAIM' },
    { kind: 'status', workflowStatus: 'WAITING_FOR_PAYMENT' },
];

export const WORKFLOW_STATUS_DESCRIPTIONS: Record<string, string> = {
    NEEDS_VENDOR_CODING:
        'Sends the latest valid HL7 encounter to an external coding vendor so a billable claim can be created.',
    NEEDS_ENTER_CODING:
        'Creates an Enter coding request from the latest or missed HL7 so the encounter can be coded before billing.',
    EXECUTE_ENTER_CODING:
        'Runs Enter’s coding engine and posts the resulting professional claim data back to the claim.',
    WAIT_FOR_PROFESSIONAL_CREATED:
        'Pauses automation until the professional claim exists, then continues coding or downstream billing work.',
    HALTED: 'Holds the claim from further processing until an authorized provider resumes it.',
    TIMELY_FILING: 'Submits the claim, or uses fallback delivery, to preserve timely filing rights.',
    NEED_MEDICAL_RECORDS: 'Requests, gathers, and attaches medical records needed for payer review.',
    NEED_PRIMARY_AUTHORIZATION_NUMBER:
        'Finds or requests the missing primary prior authorization number and stores it on the claim.',
    NEED_PRIMARY_ELIGIBILITY_CHECK:
        'Runs or reviews primary eligibility and routes coverage issues for correction before submission.',
    HIGH_VALUE_CLAIM_SPLIT:
        'Splits high-dollar service lines into related claims to avoid payer or clearinghouse amount limits.',
    NEED_PRIMARY_TRANSMIT:
        'Sends the claim to the primary payer or routes provider-fixable issues for correction.',
    NEED_PRIMARY_NON_ACKNOWLEDGED_TRANSMIT:
        'Retransmits a primary claim when prior sends have not received payer acknowledgement.',
    REJECTION_UNENROLLED_PAYER:
        'Waits for payer enrollment to be resolved, then resubmits the claim to the primary payer.',
    NEED_PRIMARY_RETRANSMIT:
        'Resubmits the primary claim as an original or replacement after a prior submission needs correction.',
    PAYMENT_DENIED_FOR_HOSPICE_CARE:
        'Adds the hospice-related GW modifier so the denial can be corrected and rebilled.',
    PAYMENT_DENIED_FOR_LACK_OF_DOCUMENTATION:
        'Collects records, builds a documentation response packet, and sends it to the payer. Fires on RARC codes 102, M29, M30, M31, M127, N221, N391, N459, N706, N710, N729 and CARC codes 133, 226, 252.',
    PAYMENT_DENIED_FOR_NEED_NEW_SUBMISSION:
        'Resends a denied claim as a new original submission when the payer will not accept a replacement. Fires on RARC code N142.',
    NEED_SECONDARY_TRANSMIT: 'Sends or replaces the claim to the secondary payer after primary handling.',
    NEED_TERTIARY_TRANSMIT: 'Sends or replaces the claim to the tertiary payer after prior payer layers are complete.',
    NEED_GUARANTOR_COLLECTIONS:
        'Enables patient or guarantor collections when payer recovery is no longer the active path.',
    DENIED_CLAIM: 'Holds a payer-denied claim for configured support or manual follow-up.',
    REJECTED_CLAIM: 'Holds a rejected claim for configured support review or manual resolution.',
    WAITING_FOR_PAYMENT:
        'Waits for payer payment after the claim has been submitted or otherwise moved into payer processing.',
};

const EXECUTION_ORDER_INDEX = new Map<string, number>(
    CONFIGURATION_WORKER_EXECUTION_ORDER.map((entry, index) => [
        entry.kind === 'status' ? `status:${entry.workflowStatus}` : `automation:${entry.id}`,
        index,
    ]),
);

export function getWorkflowStatusExecutionIndex(workflowStatus: string): number {
    return EXECUTION_ORDER_INDEX.get(`status:${workflowStatus}`) ?? Number.MAX_SAFE_INTEGER;
}

export function getAutomationWorkerExecutionIndex(workerId: AutomationWorkerId): number {
    return EXECUTION_ORDER_INDEX.get(`automation:${workerId}`) ?? Number.MAX_SAFE_INTEGER;
}

export function compareWorkflowStatusExecutionOrder(left: string, right: string): number {
    const orderDiff =
        getWorkflowStatusExecutionIndex(left) - getWorkflowStatusExecutionIndex(right);

    if (orderDiff !== 0) {
        return orderDiff;
    }

    return left.localeCompare(right);
}
